import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LoaderService } from '@app/shared/services/loader.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AuthService } from '@app/core/auth.service';
import {
  FinanceProjectOption,
  FinanceService,
  MigrationReport,
  ReferenceEntry,
  ResourceInfoPage,
  ResourceInfoRow,
} from '../finance.service';
import {
  FilterByModalComponent,
  FilterByModalData,
  FilterByModalResult,
} from './filter-by-modal/filter-by-modal.component';
import { ImportDataModalComponent } from './import-data-modal/import-data-modal.component';
import { ShowByModalComponent, ShowByModalData, ShowByModalState } from './show-by-modal/show-by-modal.component';
import { CopyResourceModalComponent, CopyResourceModalData } from './copy-resource-modal/copy-resource-modal.component';
import { NewHireModalComponent, NewHireModalData } from './new-hire-modal/new-hire-modal.component';
import { FinanceDetailsStateService } from './finance-details-state.service';

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

@Component({
  selector: 'app-finance-details',
  templateUrl: './finance-details.component.html',
  styleUrl: './finance-details.component.scss',
})
export class FinanceDetailsComponent implements OnInit, OnDestroy {
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  /**
   * Top-of-card tab selection. The Rate Card tab is finance-admin only and
   * hidden for non-admins via *ngIf on the tab button, so this only ever
   * flips to 'rateCard' when isFinanceAdmin is true.
   */
  selectedTab: 'info' | 'rateCard' = 'info';

  /**
   * Year used to compute the 12 per-month columns (FTE / Hours / One Time /
   * Total) appended after the existing 38 canonical columns. Drives both
   * the column headers ("Jan 26 FTE", "Jan 26 Hours", …) and the BE query.
   */
  selectedYear: number = new Date().getFullYear();

  /** Picker options — 5 prior years + current + 1 future. Plenty for the
   *  2021-onward historical data Product is uploading. */
  readonly yearOptions: number[] = (() => {
    const now = new Date().getFullYear();
    const years: number[] = [];
    for (let y = now + 1; y >= now - 5; y--) years.push(y);
    return years;
  })();

  /** Short-form (last two digits) of selectedYear for the column header
   *  label e.g. "Jan 26 FTE". Mirrors Product's spec. */
  get shortYear(): string {
    return String(this.selectedYear % 100).padStart(2, '0');
  }

  /** Static month labels for the column header. */
  readonly monthLabels: readonly string[] = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  /** Per-month metric keys + display labels. The matColumnDef id format is
   *  `monthCell_<monthIndex>_<metricKey>` — the template iterates these
   *  arrays to render 48 cells per row. */
  readonly monthMetrics: ReadonlyArray<{ key: 'fte' | 'hours' | 'oneTime' | 'total'; label: string }> = [
    { key: 'fte', label: 'FTE' },
    { key: 'hours', label: 'Hours' },
    { key: 'oneTime', label: 'One Time' },
    { key: 'total', label: 'Total' },
  ];

  /**
   * Toggles the "expanded" view that exposes Jan..pivot months. When
   * false, only the pivot month's 4 columns are visible (default). The
   * pivot month is today's month for the current year, December for
   * a past year, and January for a future year — see {@link pivotMonth}.
   * Future months are never rendered.
   */
  showPastMonths = false;

  /**
   * Month index (0-11) of the latest month that should be visible for
   * the selectedYear. Visible-by-default when {@link showPastMonths} is
   * false; the expand toggle reveals indices 0..pivotMonth-1 in addition.
   */
  get pivotMonth(): number {
    const now = new Date();
    if (this.selectedYear < now.getFullYear()) return 11; // past year — show all the way to December
    if (this.selectedYear > now.getFullYear()) return 0;  // future year — show only January as a placeholder
    return now.getMonth(); // current year — today's month (0-indexed)
  }

  /** Month indices (0-11) the table currently renders columns for.
   *  Drives both the matColumnDef ids in {@link displayedColumns} and the
   *  *ngFor in the template — same array on both sides keeps them in sync. */
  get visibleMonthIndices(): number[] {
    const pivot = this.pivotMonth;
    if (!this.showPastMonths) return [pivot];
    const out: number[] = [];
    for (let m = 0; m <= pivot; m++) out.push(m);
    return out;
  }

  /** True iff there's at least one past month under the pivot — drives
   *  whether the expand toggle button is rendered at all. A future year
   *  has pivot=0 so the toggle disappears. */
  get hasPastMonths(): boolean {
    return this.pivotMonth > 0;
  }

  toggleShowPastMonths(): void {
    this.showPastMonths = !this.showPastMonths;
  }

  rows: ResourceInfoRow[] = [];
  totalElements = 0;
  totalPages = 1;
  page = 0;
  size = DEFAULT_PAGE_SIZE;

  searchText = '';
  /**
   * Currently-applied set of filter-by-modal selections. Empty object means
   * no filters; populated keys mirror ResourceInfoQuery field names so we can
   * spread directly into the API call.
   */
  appliedFilters: FilterByModalResult = {};

  /** Current sort column ('' = default 3-tier rule on the BE). */
  sortBy = '';
  /** "asc" | "desc"; ignored when sortBy is blank. */
  sortDir: 'asc' | 'desc' | '' = '';

  /**
   * Reference-data backing for the two inline quick-access filter dropdowns
   * in the section header (Projects + LOB). The Filter-by modal loads the
   * same lists on its own ngOnInit; both UIs read/write the same keys on
   * appliedFilters so selections stay consistent either way.
   *
   * Projects stays empty until the Project Catalog list endpoint ships;
   * the dropdown then renders a disabled placeholder.
   */
  supportingLobOptions: ReferenceEntry[] = [];
  projectOptions: FinanceProjectOption[] = [];

  /** In-panel search box on the Projects dropdown — bound below to filter
   *  the rendered options as the operator types. Keeps the multi-select
   *  panel usable when there are dozens / hundreds of projects. */
  projectFilterText = '';

  /** Lowercase + trimmed view of {@link projectFilterText} so the per-
   *  option match check isn't recomputed for every option on every
   *  keystroke. */
  get filteredProjectOptions(): FinanceProjectOption[] {
    const q = this.projectFilterText.trim().toLowerCase();
    if (!q) return this.projectOptions;
    return this.projectOptions.filter((p) =>
      (p.projectName ?? '').toLowerCase().includes(q)
      || (p.projectIdCode ?? '').toLowerCase().includes(q)
      || String(p.id).includes(q),
    );
  }

  /** Resets the inline search when the dropdown panel closes — otherwise
   *  the next open would still show only the previously-typed subset. */
  onProjectsPanelOpenedChange(opened: boolean): void {
    if (!opened) this.projectFilterText = '';
  }

  loading = false;
  errorMessage: string | null = null;

  /** Bound from the template so Angular Material's MatSort directive can drive sortChange events. */
  @ViewChild(MatSort) sort?: MatSort;
  /** Bound from the template so PageEvent values flow back through the same load() path. */
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  /**
   * Full column set in Figma left-to-right order. The first 5 are sticky on
   * horizontal scroll (matColumnDef sticky attribute is set in the template);
   * the rest scroll out as the user pans right. The monthly FTE columns
   * (Jan-Dec) and the Total & One Time $ column are intentionally absent —
   * they need a schema addition + derivation logic that's queued as a
   * follow-up story.
   */
  /**
   * Sticky first 5 columns — always visible, not togglable via Show by.
   * Order matches the matColumnDef sticky declarations in the template.
   */
  readonly stickyColumns: string[] = [
    'resourceName',
    'history',
    'edit',
    'copyTo',
    'hiringStatus',
  ];

  /**
   * Togglable columns per Figma's Show by modal — 22 entries. Initial
   * order matches Figma's left-to-right reading order (column 1 top-to-
   * bottom, then column 2). User drag-reorder in the modal mutates
   * {@link togglableOrder}; this canonical array stays immutable so Reset
   * can restore the Figma order.
   */
  readonly togglableColumnsCanonical: string[] = [
    // Column 1 of Show by (top-to-bottom, 12 rows per Figma dev-mode)
    'supportingLob',
    'dntIndiaLeader',
    'projectNameId',
    'allocationPct',
    'capexOpex',
    'poType',
    'employer',
    'locationType',
    'tierModel',
    'activity',
    'category',
    'laptopIssued',
    // Column 2 of Show by
    'city',
    'businessSegment',
    'productSuite',
    'agileTeam',
    'reportingManager',
    'l4Manager',
    'functionalManager',
    'vp',
    'svp',
    'invoiceApprover',
    'invoiceLocation',
    'doj',
  ];

  /**
   * Always-visible columns that sit on the table but aren't in the Show by
   * modal — 15 entries. These hold critical financial data the operator
   * shouldn't be able to hide (D&T Cost, Full HR $, monthly FTE, etc.).
   * Order is the canonical Figma table position.
   */
  readonly alwaysVisibleColumns: string[] = [
    'status',
    'availableAllocationPct',
    'poiType',
    'billingStartDate',
    'billingEndDate',
    'dntCost',
    'glBusinessUnit',
    'billToFu',
    'billToMo',
    'glAccount',
    'currentMonthFte',
    'email',
    'vendorRateCard',
    'rateCardMatch',
    'fullHrAmount',
    'yearTotal',
  ];

  /**
   * Full canonical column order for the Resource Info table, used as the
   * weave template. Each id is either sticky, always-visible, or togglable.
   * The displayedColumns getter walks this and emits the actually-rendered
   * subset in the right canonical positions.
   */
  readonly canonicalColumnOrder: string[] = [
    'resourceName', 'history', 'edit', 'copyTo', 'hiringStatus',  // sticky
    'status',                                                      // always-visible
    'supportingLob', 'dntIndiaLeader', 'projectNameId',            // togglable
    'allocationPct',                                               // togglable
    'availableAllocationPct',                                      // always-visible
    'capexOpex', 'poType',                                         // togglable
    'locationType',                                                // togglable
    'poiType',                                                     // always-visible
    'employer',                                                    // togglable
    'billingStartDate', 'billingEndDate',                          // always-visible
    'tierModel',                                                   // togglable
    'dntCost', 'glBusinessUnit', 'billToFu', 'billToMo',           // always-visible
    'glAccount', 'currentMonthFte',                                // always-visible
    'activity', 'category',                                        // togglable
    'email',                                                       // always-visible
    'laptopIssued',                                                // togglable
    'vendorRateCard', 'rateCardMatch', 'fullHrAmount', 'yearTotal', // always-visible
    'city', 'businessSegment', 'productSuite', 'agileTeam',        // togglable
    'reportingManager', 'l4Manager', 'functionalManager',          // togglable
    'vp', 'svp', 'invoiceApprover', 'invoiceLocation',             // togglable
    'doj',                                                         // togglable
  ];

  /**
   * Current togglable column order — initialised to canonical, mutated by
   * drag-reorder in the Show by modal. Length always equals
   * {@link togglableColumnsCanonical}.length (22).
   */
  togglableOrder: string[] = [...this.togglableColumnsCanonical];

  /**
   * Human-readable labels for the Show by dropdown options. Keyed by column
   * id; the label matches the table's column header text so the dropdown
   * UX is unambiguous.
   */
  readonly columnLabels: Record<string, string> = {
    status: 'Status',
    supportingLob: 'Supporting LOB',
    dntIndiaLeader: 'D&T India Leader',
    projectNameId: 'Project Name & ID',
    allocationPct: 'Allocation',
    availableAllocationPct: 'Available Allocation',
    capexOpex: 'CAPEX/OPEX',
    poType: 'PO Type',
    locationType: 'Location Type',
    poiType: 'POI Type',
    employer: 'Employer',
    billingStartDate: 'Start Date',
    billingEndDate: 'End Date',
    tierModel: 'Tier Model',
    dntCost: 'D&T Cost',
    glBusinessUnit: 'GL BU',
    billToFu: 'Bill to FU',
    billToMo: 'Bill to MO',
    glAccount: 'GL Account',
    currentMonthFte: 'Current Month FTE',
    activity: 'Activity',
    category: 'Category',
    email: 'Email ID',
    laptopIssued: 'Laptop Issued',
    vendorRateCard: 'Vendor Rate Card',
    rateCardMatch: 'Rate Card Match',
    fullHrAmount: 'Full HR $',
    yearTotal: 'Total $',
    city: 'City',
    businessSegment: 'Business SEG',
    productSuite: 'Product Suite',
    agileTeam: 'Agile Team',
    reportingManager: 'RM',
    l4Manager: 'L4 MGR',
    functionalManager: 'Functional MGR',
    vp: 'VP',
    svp: 'SVP',
    invoiceApprover: 'D&T Invoice Approver',
    invoiceLocation: 'Invoice Location',
    doj: 'DOJ',
  };

  /**
   * Currently-visible togglable column ids — subset of {@link togglableOrder}.
   * Default = all 22 visible.
   */
  visibleTogglableColumns: string[] = [...this.togglableColumnsCanonical];

  /** Storage key for the column-visibility + order preference. */
  private static readonly COLUMN_VIS_STORAGE_KEY = 'finance-details-show-by-v2';

  /**
   * Returns the column ids actually passed to mat-table. Walks the canonical
   * column order: emits sticky and always-visible columns at their fixed
   * positions, and uses {@link togglableOrder} (user-reordered) for the
   * togglable slots. A hidden togglable column is skipped (its slot
   * collapses).
   */
  get displayedColumns(): string[] {
    const togglableSet = new Set(this.togglableColumnsCanonical);
    const visibleSet = new Set(this.visibleTogglableColumns);
    // Pull togglable ids in user-chosen order, filtered to visible ones,
    // ready to be consumed in canonical-position slots.
    const togglableQueue = this.togglableOrder.filter((id) => visibleSet.has(id));
    let qi = 0;
    const result: string[] = [];
    for (const id of this.canonicalColumnOrder) {
      if (togglableSet.has(id)) {
        // Consume the next user-ordered togglable slot, if any remain.
        if (qi < togglableQueue.length) {
          result.push(togglableQueue[qi++]);
        }
      } else {
        result.push(id);
      }
    }
    // Thin inline expand/collapse chevron column. Sits just before the
    // monthly cells. Hidden when there are no past months to expand into
    // (viewing a future year — pivot = January, nothing earlier).
    if (this.hasPastMonths) result.push('monthToggle');

    // Append the monthly cells for the currently visible months. Default
    // is just the pivot month (4 columns); the expand toggle adds prior
    // months back to January. matColumnDef id = monthCell_<m>_<metricKey>.
    for (const m of this.visibleMonthIndices) {
      for (const metric of this.monthMetrics) {
        result.push(`monthCell_${m}_${metric.key}`);
      }
    }
    return result;
  }

  /** Fires when the year picker changes — resets to page 0 and reloads
   *  so the months[] data from the BE matches the selected year. */
  onYearChange(): void {
    this.page = 0;
    this.load();
  }

  /** Helper for the template — formats one cell value (FTE / Hours /
   *  One Time / Total) for display. FTE is a fraction shown to 2 dp,
   *  Hours is an integer-ish number, money values get the $ prefix. */
  formatMonthCell(value: number | undefined | null, key: string): string {
    if (value == null) return '';
    switch (key) {
      case 'fte':
        return Number(value).toFixed(2);
      case 'hours':
        return Number(value).toFixed(1);
      case 'oneTime':
      case 'total':
        return `$${Number(value).toFixed(2)}`;
      default:
        return String(value);
    }
  }

  private readonly searchInput$ = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly financeService: FinanceService,
    private readonly loaderService: LoaderService,
    private readonly toaster: ToasterService,
    private readonly authService: AuthService,
    private readonly stateService: FinanceDetailsStateService,
  ) {
    this.loaderService.hide();
  }

  get isFinanceAdmin(): boolean {
    return !!this.authService.userProfile?.isFinanceAdmin;
  }

  /**
   * Navigates to the full-page Project History view for the row's resource.
   * The page route owns the data fetch + table; this method just hops over.
   * No-op when the row has no resourceId (new-hire / unmapped resources have
   * no history to show).
   */
  navigateToHistory(row: ResourceInfoRow): void {
    if (!row.resourceId) return;
    this.router.navigate(['/finance/details/history', row.resourceId], {
      queryParams: { name: row.resourceName },
    });
  }

  /**
   * Navigates to the full-page Edit Project view for the row's resource.
   * Same pattern as navigateToHistory — the page route owns load + save.
   * No-op when the row has no resourceId (new-hire rows can't be edited
   * here yet — the New Project tab is the future home for that).
   */
  navigateToEdit(row: ResourceInfoRow): void {
    // New-hire rows (no active assignment) get the New Hire Form modal
    // instead of the Edit Project page — the page expects an existing
    // assignment to edit. The modal provisions the first assignment +
    // rate card; on success we refresh the listing so the row moves
    // out of the new-hire bucket.
    if (row.isNewHire) {
      if (!row.employeeId) return;
      this.openNewHireModal(row);
      return;
    }
    if (!row.resourceId) return;
    this.router.navigate(['/finance/details/edit', row.resourceId], {
      queryParams: { name: row.resourceName },
    });
  }

  private openNewHireModal(row: ResourceInfoRow): void {
    const employeeId = row.employeeId;
    if (!employeeId) return;
    const ref = this.dialog.open<
      NewHireModalComponent,
      NewHireModalData,
      unknown
    >(NewHireModalComponent, {
      data: {
        employeeId,
        resourceName: row.resourceName,
      },
      // No width/maxWidth/maxHeight here — those are controlled entirely
      // via the .finance-new-hire-modal-panel rule in styles.scss so we
      // don't fight Material's inline-style precedence.
      panelClass: 'finance-new-hire-modal-panel',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.page = 0;
        this.load();
      }
    });
  }

  /**
   * Opens the Copy Resource Data modal sourced from this row. On
   * confirm the BE clones the source's active assignments + rate-card
   * periods to the target chosen in the modal; we then refresh the
   * table so the target's new rows are immediately visible (they may
   * paginate off-screen but the data is live).
   */
  openCopyModal(row: ResourceInfoRow): void {
    // Copy is per-assignment, so the row must carry both a resourceId
    // and an assignmentId. New-hire rows + continuation rows are
    // disabled at the icon level so this guard is defensive only.
    if (!row.resourceId || !row.assignmentId) return;

    // Build the field preview rendered as checkboxes in the modal —
    // mirrors the Figma's "deselect to exclude" UX. Values come from
    // the row's current join; the BE clones them along with the
    // assignment + rate card.
    const fieldPreview = this.buildCopyFieldPreview(row);

    const ref = this.dialog.open<
      CopyResourceModalComponent,
      CopyResourceModalData,
      unknown
    >(CopyResourceModalComponent, {
      data: {
        sourceResourceId: row.resourceId,
        sourceAssignmentId: row.assignmentId,
        sourceResourceName: row.resourceName,
        sourceEmployeeId: row.employeeId,
        sourceProjectName: row.projectName,
        sourceProjectIdCode: row.projectIdCode,
        fieldPreview,
      },
      // Sized to use the full available viewport so the 2-column field
      // grid has room to render long values (project name + id, GL
      // codes, etc.) on a single line without truncation. 2000px is
      // never the actual rendered width on a laptop — maxWidth: 99vw
      // is what kicks in — but the explicit width keeps the modal as
      // wide as the screen allows.
      width: '2000px',
      maxWidth: '99vw',
      maxHeight: '96vh',
      panelClass: 'finance-copy-modal-panel',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh so newly-copied rows for the target appear in the
        // Resource Info table without a manual refetch.
        this.load();
      }
    });
  }

  /**
   * Builds the list of field-preview chips shown in the Copy modal.
   * Matches the Figma's field set: project-level attributes that the
   * target will inherit by virtue of being assigned to the same
   * project, plus a couple of per-assignment numbers. Order kept
   * Figma-faithful so the operator's eye lands on the same fields.
   */
  private buildCopyFieldPreview(row: ResourceInfoRow): Array<{ label: string; value: string }> {
    return [
      { label: 'Business Segment',     value: row.businessSegment ?? '' },
      { label: 'Supporting LOB',       value: row.supportingLob ?? '' },
      { label: 'SVP',                  value: row.svp ?? '' },
      { label: 'VP',                   value: row.vp ?? '' },
      { label: 'Functional Manager',   value: row.functionalManager ?? '' },
      { label: 'D&T Invoice Approver', value: row.invoiceApprover ?? '' },
      { label: 'Product Suite',        value: row.productSuite ?? '' },
      { label: 'Agile Team',           value: row.agileTeam ?? '' },
      { label: 'Project Name & ID',    value: this.formatProjectLabel(row) },
      { label: 'Region',               value: row.region ?? '' },
      { label: 'GL BU',                value: row.glBusinessUnit ?? '' },
      { label: 'Bill to FU',           value: row.billToFu ?? '' },
      { label: 'Bill to MO',           value: row.billToMo ?? '' },
    ];
  }

  private formatProjectLabel(row: ResourceInfoRow): string {
    if (!row.projectName && !row.projectIdCode) return '';
    if (!row.projectIdCode) return row.projectName ?? '';
    if (!row.projectName) return row.projectIdCode ?? '';
    return `${row.projectName} | ${row.projectIdCode}`;
  }

  openImportData(): void {
    const ref = this.dialog.open<ImportDataModalComponent, void, MigrationReport | null>(
      ImportDataModalComponent,
      {
        width: '720px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'finance-import-data-modal-panel',
        autoFocus: false,
        disableClose: true,
      },
    );
    ref.afterClosed().subscribe((report) => {
      if (report && (report.imported > 0 || report.skippedIdempotent > 0)) {
        // Refresh the table so the newly-imported rows show up immediately.
        this.page = 0;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.page = 0;
          this.load();
        }),
    );
    // Reference data for the inline quick-filter dropdowns. Loaded once
    // here; the Filter-by modal reloads them when it opens (acceptable
    // duplication — keeps the modal self-contained).
    this.financeService.listReferenceEntries('supporting-lob').subscribe({
      next: (opts) => (this.supportingLobOptions = opts),
      error: () => (this.supportingLobOptions = []),
    });
    // Project Catalog now ships /finance/projects — feed the inline
    // Projects quick-filter from it instead of leaving it empty.
    this.financeService.listFinanceProjects().subscribe({
      next: (opts) => (this.projectOptions = opts),
      error: () => (this.projectOptions = []),
    });
    this.restoreColumnVisibility();
    // Restore the working session state (search / filters / sort / page +
    // size) so navigating Project History → Back doesn't reset the
    // operator's context. First visit of the session sees no state and
    // falls through to defaults.
    this.restoreSessionState();
    this.load();
  }

  /** Pulls saved state from the in-memory store into local fields. */
  private restoreSessionState(): void {
    const saved = this.stateService.getState();
    if (!saved) return;
    this.searchText = saved.searchText;
    this.appliedFilters = saved.appliedFilters;
    this.sortBy = saved.sortBy;
    this.sortDir = saved.sortDir;
    this.page = saved.page;
    this.size = saved.size;
  }

  /** Mirrors current local fields into the in-memory store. Called on
   *  every load() so the latest user choices are always preserved. */
  private persistSessionState(): void {
    this.stateService.setState({
      searchText: this.searchText,
      appliedFilters: this.appliedFilters,
      sortBy: this.sortBy,
      sortDir: this.sortDir,
      page: this.page,
      size: this.size,
    });
  }

  /**
   * Opens the Show by modal pre-populated with the current order + visible
   * state. On Apply, the user's reordered + filtered selections take effect
   * immediately and persist to localStorage. Cancel discards changes.
   */
  openShowByModal(): void {
    const ref = this.dialog.open<ShowByModalComponent, ShowByModalData, ShowByModalState | null>(
      ShowByModalComponent,
      {
        data: {
          initial: {
            order: [...this.togglableOrder],
            visible: [...this.visibleTogglableColumns],
          },
          labels: this.columnLabels,
        },
        width: '720px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'finance-show-by-modal-panel',
        autoFocus: false,
      },
    );
    ref.afterClosed().subscribe((result) => {
      if (result) {
        // Sanity-filter against the canonical set in case storage carries
        // an id that's since been removed from the togglable list.
        const validSet = new Set(this.togglableColumnsCanonical);
        const safeOrder = result.order.filter((id) => validSet.has(id));
        // If any canonical id is missing from the persisted order (added
        // post-release), append it so the user doesn't lose visibility on
        // newer columns.
        for (const id of this.togglableColumnsCanonical) {
          if (!safeOrder.includes(id)) safeOrder.push(id);
        }
        const safeVisible = result.visible.filter((id) => validSet.has(id));
        this.togglableOrder = safeOrder;
        this.visibleTogglableColumns = safeVisible;
        this.persistShowByPreference();
      }
    });
  }

  private persistShowByPreference(): void {
    try {
      localStorage.setItem(
        FinanceDetailsComponent.COLUMN_VIS_STORAGE_KEY,
        JSON.stringify({
          order: this.togglableOrder,
          visible: this.visibleTogglableColumns,
        }),
      );
    } catch {
      // localStorage unavailable (private mode, quota) — in-memory state
      // still applies, the preference just won't survive a reload.
    }
  }

  private restoreColumnVisibility(): void {
    try {
      const raw = localStorage.getItem(FinanceDetailsComponent.COLUMN_VIS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { order?: unknown; visible?: unknown };
      if (!parsed || typeof parsed !== 'object') return;
      const validSet = new Set(this.togglableColumnsCanonical);
      if (Array.isArray(parsed.order)) {
        const restoredOrder = (parsed.order as unknown[])
          .filter((v): v is string => typeof v === 'string' && validSet.has(v));
        // Append any canonical ids not yet in storage (handles post-release
        // additions of new togglable columns).
        for (const id of this.togglableColumnsCanonical) {
          if (!restoredOrder.includes(id)) restoredOrder.push(id);
        }
        this.togglableOrder = restoredOrder;
      }
      if (Array.isArray(parsed.visible)) {
        const restoredVisible = (parsed.visible as unknown[])
          .filter((v): v is string => typeof v === 'string' && validSet.has(v));
        this.visibleTogglableColumns = restoredVisible;
      }
    } catch {
      // Stored value malformed; fall back to default (all visible, canonical order).
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSearchChange(): void {
    this.searchInput$.next(this.searchText);
  }

  clearSearch(): void {
    this.searchText = '';
    this.page = 0;
    this.load();
  }

  /** Selected project ids — bound to the inline Projects dropdown. */
  get selectedProjectIds(): number[] {
    return this.appliedFilters.projectIds ?? [];
  }
  set selectedProjectIds(values: number[]) {
    this.appliedFilters = { ...this.appliedFilters, projectIds: values };
    this.page = 0;
    this.load();
  }

  /** Selected supporting LOB codes — bound to the inline LOB dropdown. */
  get selectedSupportingLobs(): string[] {
    return this.appliedFilters.supportingLobs ?? [];
  }
  set selectedSupportingLobs(values: string[]) {
    this.appliedFilters = { ...this.appliedFilters, supportingLobs: values };
    this.page = 0;
    this.load();
  }

  openFilterModal(): void {
    const ref = this.dialog.open<FilterByModalComponent, FilterByModalData, FilterByModalResult>(
      FilterByModalComponent,
      {
        data: { initial: this.appliedFilters },
        // No width/maxWidth here — the outer Material surface is
        // transparent (per styles.scss panel rule), and the inner
        // .filter-modal owns the visible card sizing in its component
        // SCSS. Bypasses Material's inline-style precedence games.
        maxHeight: '90vh',
        panelClass: 'finance-filter-modal-panel',
        autoFocus: false,
      },
    );
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.appliedFilters = result;
        this.page = 0;
        this.load();
      }
    });
  }

  resetAllFilters(): void {
    this.appliedFilters = {};
    this.searchText = '';
    this.page = 0;
    this.load();
  }

  onPageSizeChange(newSize: number): void {
    this.size = newSize;
    this.page = 0;
    this.load();
  }

  /** Called by mat-paginator. Page index + size both flow through here. */
  onPageEvent(event: PageEvent): void {
    this.page = event.pageIndex;
    this.size = event.pageSize;
    this.load();
  }

  /**
   * Called by MatSort when a header is clicked. Angular Material cycles
   * through asc → desc → '' (cleared), so we mirror that to keep the BE's
   * default 3-tier rule reachable by a third click.
   */
  onSortChange(event: Sort): void {
    this.sortBy = event.direction ? event.active : '';
    this.sortDir = event.direction || '';
    this.page = 0;
    this.load();
  }

  goToPage(target: number): void {
    if (target < 0 || target >= this.totalPages || target === this.page) {
      return;
    }
    this.page = target;
    this.load();
  }

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  /** View Projects → Project Details listing page. */
  navigateToProjects(): void {
    this.router.navigate(['/finance/details/projects']);
  }

  /** Tier Rates admin page — finance-admin only (button is hidden for
   *  non-admins; route is also gated by AuthGuard + admin check on BE). */
  navigateToTierRates(): void {
    this.router.navigate(['/finance/details/tier-rates']);
  }

  /**
   * Download logic is scoped to a separate spec per the Figma callout;
   * surface a toast so the affordance reads as discoverable without
   * claiming functionality.
   */
  comingSoon(label: string): void {
    this.toaster.showInfo(`${label} — coming soon`, 'Finance Details');
  }

  get activeFilterCount(): number {
    // Count populated keys on appliedFilters. List-valued slots count when
    // non-empty; text slots when non-blank; boolean slots when true. The
    // shape of FilterByModalResult guarantees only "active" keys are present
    // (the modal strips empties at Apply time).
    let count = 0;
    for (const value of Object.values(this.appliedFilters)) {
      if (Array.isArray(value)) {
        if (value.length > 0) count++;
      } else if (typeof value === 'string') {
        if (value.trim()) count++;
      } else if (typeof value === 'boolean') {
        if (value) count++;
      }
    }
    return count;
  }

  get pageStart(): number {
    return this.totalElements === 0 ? 0 : this.page * this.size + 1;
  }

  get pageEnd(): number {
    return Math.min((this.page + 1) * this.size, this.totalElements);
  }

  formatAllocation(value: number | undefined | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    return `${Math.round(value)}%`;
  }

  /**
   * Label for the monthly FTE column header. We prefer the month echoed back
   * from the BE (the JOIN's actual matched month — guarantees the header
   * matches the value the row actually carries). Falls back to the FE's
   * notion of "now" when no row has matched yet (empty result set).
   */
  get currentMonthLabel(): string {
    const sample = this.rows.find((r) => r.currentMonthFteMonth);
    const month = sample?.currentMonthFteMonth ?? new Date().getMonth() + 1;
    return new Date(2000, month - 1, 1).toLocaleString('en-US', { month: 'short' });
  }

  formatMonthlyFte(value: number | undefined | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    // Source data carries a decimal fraction (0.4 = 40%). Render as integer %
    // for table density; tooltip on the cell shows the precise value.
    return `${Math.round(Number(value) * 100)}%`;
  }

  formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    return `$${Number(value).toFixed(1)}`;
  }

  formatDate(iso: string | undefined | null): string {
    if (!iso) {
      return '';
    }
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) {
      return '';
    }
    const date = new Date(y, m - 1, d);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month} ${date.getFullYear()}`;
  }

  /**
   * Empty-cell rendering rule per Figma: rows that lack an active assignment
   * show literal "TBD" (this is the new-hire / unassigned case), while rows
   * that have an assignment but happen to lack one specific column show "—".
   * Distinguishes "this whole row is pending" from "this field happens to be
   * blank on an otherwise-populated row".
   */
  tbdOrDash(row: ResourceInfoRow): string {
    return row.isNewHire || !row.assignmentId ? 'TBD' : '—';
  }

  /**
   * Composed tooltip for the Resource cell — name, employee id, and role
   * concatenated. Visible to users when the inline ellipsis truncates a
   * long value; gives them the full string on hover without widening the
   * column.
   */
  resourceTooltip(row: ResourceInfoRow): string {
    const parts: string[] = [];
    if (row.resourceName) parts.push(row.resourceName);
    if (row.employeeId) parts.push(row.employeeId);
    if (row.role) parts.push(row.role);
    return parts.join(' | ');
  }

  /**
   * Maps a hiring-status code to the Material icon shown inside its pill.
   * Codes are the same internal values the migration importer canonicalises
   * from the source workbook (JOINED → check_circle, etc.).
   */
  hiringStatusIcon(code: string | undefined | null): string {
    switch (code) {
      case 'JOINED':                return 'check_circle';
      case 'EXCLUDED':
      case 'RELEASED':
      case 'BACKED_OUT':            return 'cancel';
      case 'HOLD':                  return 'pause_circle';
      case 'MANUAL_ENTRY':
      case 'RELEASED_MANUAL_ENTRY': return 'edit_note';
      default:                      return 'help_outline';
    }
  }

  formatHiringStatus(code: string | undefined | null): string {
    if (!code) return '';
    // Display label = lowercased & title-cased version of the code,
    // mapping a couple of less-readable codes to the Figma wording.
    switch (code) {
      case 'JOINED':                 return 'Joined';
      case 'EXCLUDED':               return 'Excluded';
      case 'RELEASED':               return 'Released';
      case 'BACKED_OUT':             return 'Backed Out';
      case 'HOLD':                   return 'Hold';
      case 'MANUAL_ENTRY':           return 'Manual Entry';
      case 'RELEASED_MANUAL_ENTRY':  return 'Released — Manual';
      default:                       return code;
    }
  }

  reviewStatusIcon(code: string | undefined | null): string {
    switch (code) {
      case 'REVIEW_COMPLETED': return 'task_alt';
      case 'PENDING_REVIEW':
      case 'OPEN':             return 'pending';
      default:                 return 'help_outline';
    }
  }

  formatReviewStatus(code: string | undefined | null): string {
    if (!code) return '';
    switch (code) {
      case 'REVIEW_COMPLETED': return 'Review Completed';
      case 'PENDING_REVIEW':
      case 'OPEN':             return 'Pending Review';
      default:                 return code;
    }
  }

  formatCapexOpex(code: string | undefined | null): string {
    if (!code) return '';
    switch (code) {
      case 'CAPEX':     return 'CAPEX';
      case 'PROJ_OPEX': return 'OPEX';
      case 'RTB_OPEX':  return 'RTB OPEX';
      default:          return code;
    }
  }

  /**
   * Marks rows whose employeeId matches the immediately-prior row as
   * "continuation" rows. The template uses this flag to suppress the
   * Resource / History / Edit cells on continuation rows so a multi-
   * assignment resource looks like one rowspan-style group. Copy to stays
   * visible on every row regardless — each assignment is independently
   * copyable. Continuation grouping breaks when sort scatters the rows
   * (e.g. when sorted by allocation) — that's intentional, since rowspan
   * grouping is only meaningful when same-resource rows are adjacent.
   */
  private markContinuations(rows: ResourceInfoRow[]): ResourceInfoRow[] {
    let prevEmployeeId: string | undefined;
    return rows.map((row) => {
      const isContinuation = !!prevEmployeeId && row.employeeId === prevEmployeeId;
      prevEmployeeId = row.employeeId;
      return { ...row, isContinuation };
    });
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;
    // Every load is the freshest snapshot of search/filter/sort/page state,
    // so persist here once and we never miss a code path that mutates state
    // (search debounce, filter modal apply, sort header click, pagination,
    // page-size change, reset, inline dropdown setter).
    this.persistSessionState();
    this.financeService
      .listResourceInfo({
        ...this.appliedFilters,
        search: this.searchText,
        page: this.page,
        size: this.size,
        sortBy: this.sortBy || undefined,
        sortDir: this.sortDir || undefined,
        year: this.selectedYear,
      })
      .subscribe({
        next: (data: ResourceInfoPage) => {
          this.rows = this.markContinuations(data.rows ?? []);
          this.totalElements = data.totalElements ?? 0;
          this.totalPages = Math.max(1, data.totalPages ?? 1);
          this.page = data.page ?? 0;
          this.size = data.size ?? this.size;
          this.loading = false;
        },
        error: (err) => {
          console.error('Resource Info load failed', err);
          this.errorMessage = 'Unable to load resource info. Please try again.';
          this.loading = false;
          this.toaster.showError('Failed to load resource info', 'Error');
        },
      });
  }
}
